"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowRight, Stethoscope, Users, Brain, DollarSign, Check, TrendingDown, Award, Smile, Clock, PiggyBank, BarChart3, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PageHero from "@/components/landing/PageHero";
import PricingCards from "@/components/landing/PricingCards";
import { AnimatedSection } from "@/hooks/useScrollAnimation";
import { makeServiceT } from "@/data/serviceDetails";
const businessHero = "/assets/business-presentation.jpg";
const businessAboutImg = "/assets/owner-smiling.jpg";

const Businesses = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const scrollToContact = () => { router.push("/contact"); };

  const features = [
    { icon: Stethoscope, title: t("businesses.feature1Title"), description: t("businesses.feature1Desc") },
    { icon: Users, title: t("businesses.feature2Title"), description: t("businesses.feature2Desc") },
    { icon: Brain, title: t("businesses.feature3Title"), description: t("businesses.feature3Desc") },
    { icon: DollarSign, title: t("businesses.feature4Title"), description: t("businesses.feature4Desc") },
  ];

  const steps = [
    {
      step: t("businesses.step1Label"),
      title: t("businesses.step1Title"),
      description: t("businesses.step1Desc"),
      bullets: [
        t("businesses.step1Bullet1"),
        t("businesses.step1Bullet2"),
        t("businesses.step1Bullet3"),
      ],
      footer: t("businesses.step1Footer"),
    },
    {
      step: t("businesses.step2Label"),
      title: t("businesses.step2Title"),
      description: t("businesses.step2Desc"),
      bullets: [
        t("businesses.step2Bullet1"),
        t("businesses.step2Bullet2"),
        t("businesses.step2Bullet3"),
        t("businesses.step2Bullet4"),
      ],
      footer: t("businesses.step2Footer"),
    },
    {
      step: t("businesses.step3Label"),
      title: t("businesses.step3Title"),
      description: t("businesses.step3Desc"),
      bullets: [
        t("businesses.step3Bullet1"),
        t("businesses.step3Bullet2"),
        t("businesses.step3Bullet3"),
      ],
      footer: t("businesses.step3Footer"),
    },
  ];

  const whyEmployers = [
    { icon: TrendingDown, title: t("businesses.why1Title"), description: t("businesses.why1Desc") },
    { icon: Award, title: t("businesses.why2Title"), description: t("businesses.why2Desc") },
    { icon: Smile, title: t("businesses.why3Title"), description: t("businesses.why3Desc") },
    { icon: Clock, title: t("businesses.why4Title"), description: t("businesses.why4Desc") },
    { icon: PiggyBank, title: t("businesses.why5Title"), description: t("businesses.why5Desc") },
    { icon: BarChart3, title: t("businesses.why6Title"), description: t("businesses.why6Desc") },
  ];

  const plans = [
    {
      name: t("pricing.essentialsName"),
      price: "$25",
      period: t("businesses.perEePerMonth"),
      subtitle: t("pricing.essentialsSubtitle"),
      services: ["Telemedicine", "Dermatology"].map(n => makeServiceT(n, t)),
      popular: false,
    },
    {
      name: t("pricing.advantageName"),
      price: "$35",
      period: t("businesses.perEePerMonth"),
      subtitle: t("businesses.advantageSubtitle"),
      services: ["Telemedicine", "Dermatology", "Behavioral Health", "Pharmacy Savings", "Healthcare Concierge"].map(n => makeServiceT(n, t)),
      popular: false,
    },
    {
      name: t("pricing.premierName"),
      price: "$45",
      period: t("businesses.perEePerMonth"),
      subtitle: t("pricing.premierSubtitle"),
      services: ["Telemedicine", "Dermatology", "Behavioral Health", "Pharmacy Savings", "Healthcare Concierge", "Lab Testing", "Wellness Coaching", "Best Price MRI/Imaging", "Medical Bill Negotiations", "Medical Bill Financing"].map(n => makeServiceT(n, t)),
      popular: false,
    },
  ];

  const testimonials = [
    { text: "As a construction company, we couldn't afford to offer full major medical benefits, but we still wanted to provide real support for our crew. Legacy Healthcare Solutions gave us an affordable way to offer 24/7 doctor access for employees and their families. It's been a valuable benefit that helps us attract and keep good workers without putting pressure on our margins.", name: "Jason M.", role: "Construction Company Owner", avatar: "JM", rating: 5 },
    { text: "As an HR Director, my biggest concern was adding more administrative work. This was the opposite. Implementation was straightforward, and employees access services directly — we're not managing claims or fielding constant questions. The virtual visits have reduced time away from work, and our employees genuinely use the mental health services. It filled a gap we didn't even realize was costing us productivity.", name: "Sarah L.", role: "HR Director", avatar: "SL", rating: 5 },
    { text: "Our goal is always to add meaningful value to our members without increasing dues or administrative complexity. Offering Legacy Healthcare Solutions as a voluntary benefit allowed us to provide real 24/7 healthcare access that members can choose to use for themselves and their families. It strengthened our value proposition and gave our members something practical, not just informational.", name: "David T.", role: "Association Director", avatar: "DT", rating: 5 },
  ];

  const faqs = [
    { question: t("faq.biz1Q"), answer: t("faq.biz1A") },
    { question: t("faq.biz2Q"), answer: t("faq.biz2A") },
    { question: t("faq.biz3Q"), answer: t("faq.biz3A") },
    { question: t("faq.biz4Q"), answer: t("faq.biz4A") },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      <PageHero
        title={t("businesses.heroTitle")}
        subtitle={t("businesses.heroSubtitle")}
        backgroundImage={businessHero}
      >

        <a href="https://calendly.com/astuhr-legacy-hc/telemedicine" target="_blank" rel="noopener noreferrer">
          <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-8 py-4 font-button text-base cta-hover cta-hover-glow mt-8">
            {t("businesses.scheduleCall")} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </a>
      </PageHero>

      {/* Pricing */}
      <section className="py-12 px-6 lg:px-12 bg-white" id="pricing">
        <div className="max-w-[1100px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[32px] text-foreground text-center mb-3">{t("businesses.pricingHeading")}</h2>
            <p className="font-body text-base text-foreground/70 text-center mb-10">{t("businesses.pricingSubtitle")}</p>
          </AnimatedSection>
          <PricingCards plans={plans} ctaAction={() => window.open("https://support.legacy-hc.com/widget/form/TgnMv3pkwXzQzfAvbwXs", "_blank")} hideCta />
          <AnimatedSection delay={300}>
            <div className="text-center mt-8">
              <a href="https://calendly.com/astuhr-legacy-hc/telemedicine" target="_blank" rel="noopener noreferrer">
                <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-8 py-4 font-button text-base cta-hover">
                  {t("businesses.scheduleCall")} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* How It Works — Vertical Timeline */}
      <section className="relative py-20 px-6 lg:px-12 bg-secondary overflow-hidden">
        {/* Decorative gradient circles */}
        <div className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full opacity-[0.07]" style={{ background: "radial-gradient(circle, hsl(var(--accent-blue)), transparent 70%)" }} />
        <div className="pointer-events-none absolute top-1/2 -right-32 w-[500px] h-[500px] rounded-full opacity-[0.06]" style={{ background: "radial-gradient(circle, hsl(var(--primary)), transparent 70%)" }} />
        <div className="pointer-events-none absolute -bottom-20 left-1/3 w-[360px] h-[360px] rounded-full opacity-[0.05]" style={{ background: "radial-gradient(circle, hsl(var(--accent-blue)), transparent 70%)" }} />
        <div className="max-w-[1100px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground text-center mb-16">{t("businesses.howItWorks")}</h2>
          </AnimatedSection>

          {/* Timeline container */}
          <div className="relative">

            {/* ── MOBILE timeline: left-side numbered steps ── */}
            <div className="lg:hidden flex flex-col">
              {steps.map((step, index) => (
                <AnimatedSection key={`m-${step.step}`} delay={index * 150}>
                  <div className="flex gap-4">
                    {/* Left: numbered circle + connecting line */}
                    <div className="flex flex-col items-center flex-shrink-0 w-9">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md flex-shrink-0"
                        style={{ background: "linear-gradient(135deg, hsl(var(--accent-blue)), hsl(var(--primary)))" }}
                      >
                        {index + 1}
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className="w-0.5 flex-1 mt-3 mb-1 min-h-[32px] rounded-full opacity-25"
                          style={{ background: "linear-gradient(to bottom, hsl(var(--accent-blue)), hsl(var(--primary)))" }}
                        />
                      )}
                    </div>
                    {/* Right: card */}
                    <div className="flex-1 pb-6">
                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-foreground/[0.06]">
                        <h3 className="font-heading font-bold text-base text-foreground mb-1 leading-snug">{step.title}</h3>
                        <p className="font-body text-sm text-foreground/65 leading-relaxed mb-2">{step.description}</p>
                        {step.bullets && (
                          <ul className="mt-2 space-y-1">
                            {step.bullets.map((bullet) => {
                              const parts = bullet.split("|");
                              const main = parts[0];
                              const detail = parts.slice(1).join("|");
                              return (
                                <li key={bullet} className="font-body text-sm text-foreground/65 leading-snug flex items-start gap-2">
                                  <span className="text-accent-blue mt-0.5 flex-shrink-0">•</span>
                                  <span className="font-semibold">{bullet}</span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        {step.footer && (
                          <p className="font-body text-xs text-foreground/50 leading-relaxed mt-2 italic">{step.footer}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            {/* ── DESKTOP timeline ── */}
            <div className="hidden lg:block relative">
              {/* Center vertical line */}
              <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-accent-blue via-primary to-accent-blue opacity-30" />
              <div className="flex flex-col gap-8">
                {steps.map((step, index) => {
                  const contentRight = index % 2 === 0;
                  return (
                    <AnimatedSection key={step.step} delay={index * 150}>
                      <div className="grid items-start" style={{ gridTemplateColumns: "1fr 64px 1fr" }}>

                        {/* LEFT content area */}
                        <div className={contentRight ? "pr-10" : ""}>
                          {contentRight ? (
                            <div />
                          ) : (
                            <div className="pr-10">
                              <div className="bg-white rounded-2xl p-5 shadow-sm border border-foreground/[0.06] hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                                <h3 className="font-heading font-bold text-[17px] text-foreground mb-1 leading-snug">{step.title}</h3>
                                <p className="font-body text-sm text-foreground/65 leading-relaxed mb-2">{step.description}</p>
                                {step.bullets && (
                                  <ul className="mt-2 space-y-1">
                                    {step.bullets.map((bullet) => {
                                      const parts = bullet.split("|");
                                      const main = parts[0];
                                      const detail = parts.slice(1).join("|");
                                      return (
                                        <li key={bullet} className="font-body text-sm text-foreground/65 leading-snug flex items-start gap-2">
                                          <span className="text-primary mt-0.5 flex-shrink-0">•</span>
                                          {detail ? (
                                            <><span className="font-semibold">{main.trim()}</span><span className="text-foreground/55"> {detail.trim()}</span></>
                                          ) : (
                                            <span className="font-semibold">{main.trim()}</span>
                                          )}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                                {step.footer && <p className="font-body text-xs text-foreground/50 leading-relaxed mt-2 italic">{step.footer}</p>}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* CENTER: circle on the line */}
                        <div className="flex justify-center pt-1">
                          <div
                            className="w-5 h-5 rounded-full border-2 border-white shadow-md z-10 flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, hsl(var(--accent-blue)), hsl(var(--primary)))" }}
                          />
                        </div>

                        {/* RIGHT content area */}
                        <div className={contentRight ? "pl-10" : ""}>
                          {contentRight ? (
                            <div className="pl-10">
                              <div className="bg-white rounded-2xl p-5 shadow-sm border border-foreground/[0.06] hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                                <h3 className="font-heading font-bold text-[17px] text-foreground mb-1 leading-snug">{step.title}</h3>
                                <p className="font-body text-sm text-foreground/65 leading-relaxed mb-2">{step.description}</p>
                                {step.bullets && (
                                  <ul className="mt-2 space-y-1">
                                    {step.bullets.map((bullet) => {
                                      const parts = bullet.split("|");
                                      const main = parts[0];
                                      const detail = parts.slice(1).join("|");
                                      return (
                                        <li key={bullet} className="font-body text-sm text-foreground/65 leading-snug flex items-start gap-2">
                                          <span className="text-accent-blue mt-0.5 flex-shrink-0">•</span>
                                          {main && <span className="font-semibold">{main.trim()}</span>}
                                          {detail && <span className="text-foreground/55">{detail.trim()}</span>}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                                {step.footer && <p className="font-body text-xs text-foreground/50 leading-relaxed mt-2 italic">{step.footer}</p>}
                              </div>
                            </div>
                          ) : <div />}
                        </div>

                      </div>
                    </AnimatedSection>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* About — Split Layout */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden h-[360px] lg:h-[480px]">
                <img src={businessAboutImg} alt="About Legacy Healthcare Solutions" className="w-full h-full object-cover" style={{ objectPosition: "left" }} loading="lazy" />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground mb-3">{t("businesses.aboutHeading")}</h2>
              <p className="font-body text-base text-foreground/70 mb-3">{t("businesses.aboutDesc1")}</p>
              <p className="font-body text-base text-foreground/70 mb-6">{t("businesses.aboutDesc2")}</p>
              <h3 className="font-heading font-bold text-lg text-foreground mb-4">{t("businesses.weHelp")}</h3>
              <ul className="space-y-3">
                {[t("businesses.help1"), t("businesses.help2"), t("businesses.help3"), t("businesses.help4"), t("businesses.help5"), t("businesses.help6"), t("businesses.help7")].map((item) => (
                  <li key={item} className="font-body text-sm text-foreground/80 flex items-start gap-2"><Check className="w-4 h-4 text-accent-blue mt-0.5 flex-shrink-0" />{item}</li>
                ))}
              </ul>

            </AnimatedSection>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <AnimatedSection key={feature.title} delay={i * 100}>
                <div className="group bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:bg-accent-blue cursor-pointer h-full">
                  <div className="w-12 h-12 bg-accent-blue/10 group-hover:bg-white/20 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300">
                    <feature.icon className="w-6 h-6 text-accent-blue group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading font-bold text-sm mb-2 text-foreground group-hover:text-white transition-colors duration-300">{feature.title}</h3>
                  <p className="font-body text-xs text-foreground/70 group-hover:text-white/80 leading-relaxed transition-colors duration-300">{feature.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>


      {/* Why Employers */}
      <section className="py-20 px-6 lg:px-12 bg-secondary">
        <div className="max-w-[1100px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground text-center mb-4">{t("businesses.whyEmployers")}</h2>
            <p className="font-body text-base text-foreground/70 text-center mb-12 max-w-3xl mx-auto">{t("businesses.whyEmployersSubtitle")}</p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyEmployers.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 80}>
                <div className="group bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:bg-accent-blue cursor-pointer h-full">
                  <div className="w-10 h-10 bg-accent-blue/10 group-hover:bg-white/20 rounded-xl flex items-center justify-center mb-3 transition-colors duration-300">
                    <item.icon className="w-5 h-5 text-accent-blue group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading font-bold text-sm mb-2 text-foreground group-hover:text-white transition-colors duration-300">{item.title}</h3>
                  <p className="font-body text-xs text-foreground/70 group-hover:text-white/80 leading-relaxed transition-colors duration-300">{item.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection delay={500}>
            <p className="font-body text-sm text-foreground/70 text-center mt-8">{t("businesses.associationNote")}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-12 px-6 lg:px-12 bg-secondary">
        <div className="max-w-[800px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[32px] text-foreground text-center mb-2">{t("individuals.faqHeading")}</h2>
            <p className="font-body text-base text-foreground/70 text-center mb-8">{t("individuals.faqSubtitle")}</p>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <Accordion type="single" collapsible className="mb-8">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`} className="bg-surface-card rounded-xl mb-3 px-5 border-l-4 border-l-accent-blue border-t-0 border-r-0 border-b-0 shadow-sm">
                  <AccordionTrigger className="font-heading font-medium text-sm text-foreground hover:text-accent-blue hover:no-underline py-4 text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="font-body text-xs text-foreground/70 pb-4 text-left">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <div className="text-center">
              <Link href="/faqs">
                <Button variant="outline" className="border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white rounded-full px-6 py-3 font-button text-sm cta-hover">
                  {t("individuals.importantFaqs")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-10">
            <AnimatedSection>
              <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground">
                {t("testimonials.heading")}
              </h2>
            </AnimatedSection>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <AnimatedSection key={testimonial.name} delay={i * 100}>
                <div className="flex flex-col h-full">
                  <div className="bg-surface-card rounded-xl p-6 shadow-sm flex-1">
                    <div className="flex gap-1 mb-3">
                      {[...Array(testimonial.rating)].map((_, j) => (
                        <Star key={j} className="w-4 h-4 text-accent-blue fill-accent-blue" />
                      ))}
                    </div>
                    <p className="font-body text-sm text-foreground/80 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                  </div>
                  <div className="flex items-center gap-3 mt-4 px-2">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <span className="font-heading font-bold text-xs text-white">{testimonial.avatar}</span>
                    </div>
                    <div>
                      <p className="font-heading font-medium text-sm text-foreground">{testimonial.name}</p>
                      {testimonial.role && <p className="font-body text-xs text-foreground/60">{testimonial.role}</p>}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>


      {/* Bottom CTA */}
      <section className="py-20 px-6 lg:px-12 bg-deep-blue text-white text-center">
        <AnimatedSection className="max-w-[800px] mx-auto">
          <h2 className="font-heading font-bold text-2xl lg:text-[36px] mb-4">{t("businesses.bottomCtaHeading")}</h2>
          <p className="font-body text-base text-white/80 mb-6">{t("businesses.bottomCtaSubtitle")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6 max-w-4xl mx-auto">
            {[t("businesses.bottomItem1"), t("businesses.bottomItem2"), t("businesses.bottomItem3"), t("businesses.bottomItem4"), t("businesses.bottomItem5")].map((item) => (
              <div key={item} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/10 hover:bg-white/15 transition-colors duration-300">
                <p className="font-body text-sm text-white/90">{item}</p>
              </div>
            ))}
          </div>
          <p className="font-body text-sm text-white/70 mb-6">{t("businesses.bottomNote")}</p>
          <p className="font-heading font-semibold text-lg text-white/90 mb-8">{t("businesses.bottomCta")}</p>
          <a href="https://calendly.com/astuhr-legacy-hc/telemedicine" target="_blank" rel="noopener noreferrer">
            <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-8 py-4 font-button text-base cta-hover">
              {t("businesses.scheduleStrategyCall")} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </AnimatedSection>
      </section>

      <Footer />
    </div>
  );
};

export default Businesses;
